import { useState } from "react";
import { X, Timer, Truck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const loadRzp = () =>
  new Promise((res) => {
    if (document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`)) return res(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

export const ReserveModal = ({ open, onClose, product, variant, rikshaw, onReserved }) => {
  const [busy, setBusy] = useState(false);
  if (!open || !product || !variant || !rikshaw) return null;

  const confirm = async () => {
    if (!localStorage.getItem("jow_token")) {
      toast.error("Sign in to reserve.");
      return;
    }
    setBusy(true);
    try {
      await loadRzp();
      const { data } = await api.post("/orders/reserve", {
        product_id: product.id,
        sku: variant.sku,
        qty: 1,
        rikshaw_id: rikshaw.id,
      });
      toast.success(`Reserved ${product.name} on ${rikshaw.code} for 45 mins.`);
      if (!data.mock_mode && window.Razorpay) {
        const opts = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "JewelleryOnWheels",
          description: `Hold fee · ${data.order_no}`,
          order_id: data.razorpay_order_id,
          theme: { color: "#0A1F44" },
        };
        new window.Razorpay(opts).open();
      }
      onReserved?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reserve.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-jow-navy/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="reserve-modal"
    >
      <div
        className="bg-jow-cream max-w-md w-full border border-jow-gold/40 p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <span className="label-overline text-jow-gold">Reserve on rikshaw</span>
          <button onClick={onClose} className="text-jow-navy/50" data-testid="reserve-close">
            <X size={16} />
          </button>
        </div>
        <h3 className="font-display text-2xl text-jow-navy">{product.name}</h3>
        <p className="text-sm text-jow-navy/65 mt-1">
          {variant.attrs?.color}
          {variant.attrs?.size && variant.attrs.size !== "Free Size" ? ` · ${variant.attrs.size}` : ""}
          · on <strong>{rikshaw.code}</strong> ({rikshaw.city})
        </p>
        <ul className="mt-5 space-y-2 text-sm text-jow-navy/80">
          <li className="flex items-start gap-3">
            <Truck size={14} className="text-jow-gold mt-0.5" /> Driver brings it to your door within 45 mins.
          </li>
          <li className="flex items-start gap-3">
            <Timer size={14} className="text-jow-gold mt-0.5" /> Pay ₹49 now to hold it. Hold fee is non-refundable.
          </li>
          <li className="flex items-start gap-3">
            <span className="w-3 h-3 mt-1 bg-jow-pink" /> Pay full price (₹{product.price_inr.toLocaleString("en-IN")}) only if you keep it.
          </li>
        </ul>
        <button
          disabled={busy}
          onClick={confirm}
          className="btn-primary-jow w-full mt-7"
          data-testid="reserve-confirm"
        >
          {busy ? "Reserving…" : "Pay ₹49 · Reserve now"}
        </button>
        <p className="text-[10px] text-jow-navy/45 mt-3">
          Razorpay test mode — in production this opens the real modal.
        </p>
      </div>
    </div>
  );
};
